import {
  INSPECTIONS_OVERVIEW_CHART_IDS,
  INSPECTIONS_SECTION_LABEL_ID,
} from 'charts/catalog/analyticsDashboardLayout';
import {
  getChartDefinition,
  type ChartId,
} from 'charts/catalog/chartCatalog';
import { resolveChartCardSubtitle } from 'charts/formatters/resolveChartCardSubtitle';
import { formatTranslation, unsafeTranslationKey } from 'intl';
import React, { ReactElement, useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useInspectionAnalyticsCharts } from 'charts/hooks/useInspectionAnalyticsCharts';
import { useInspectionChartRuntime } from 'charts/hooks/inspection/InspectionChartRuntimeContext';
import {
  INSPECTION_TRIPLET_CHART_ID,
  isCatalogInspectionChartId,
} from 'charts/constants/catalogInspectionCharts';
import { MemoInspectionSummarySubsection } from '../InspectionSummarySubsection';
import { MemoSectionFilters } from '../../sectionFilters/SectionFilters';
import { SectionFiltersProvider } from '../../sectionFilters/SectionFiltersContext';
import { INSPECTIONS_SECTION_FILTERS } from '../../sectionFilters/sharedFilters';
import {
  toSectionCharts,
  useSectionSharedFilters,
} from '../../sectionFilters/useSectionSharedFilters';
import { MemoAnalyticsSectionInspectionsPresentational } from './presentational';

export function AnalyticsSectionInspections(): ReactElement | null {
  const intl = useIntl();
  const charts = useInspectionAnalyticsCharts();
  const { runtimeById } = useInspectionChartRuntime();

  const overviewCharts = useMemo(
    () =>
      charts.filter(
        (chart) =>
          chart.id !== INSPECTION_TRIPLET_CHART_ID &&
          INSPECTIONS_OVERVIEW_CHART_IDS.some(
            (overviewId) => overviewId === chart.id
          )
      ),
    [charts]
  );

  const hasSummarySubsection = charts.some(
    (chart) => chart.id === INSPECTION_TRIPLET_CHART_ID
  );

  // One bar for the whole section: overview charts and the summary triplet.
  const sectionCharts = useMemo(
    () => toSectionCharts(charts, runtimeById, isCatalogInspectionChartId),
    [charts, runtimeById]
  );
  const sharedFilters = useSectionSharedFilters(
    sectionCharts,
    INSPECTIONS_SECTION_FILTERS
  );

  const resolveChartTitle = useCallback(
    (titleId: string) =>
      formatTranslation(intl, unsafeTranslationKey(titleId)),
    [intl]
  );

  const resolveChartSubtitle = useCallback(
    (chartId: ChartId) => {
      const definition = getChartDefinition(chartId);
      return resolveChartCardSubtitle(definition, intl);
    },
    [intl]
  );

  return (
    <SectionFiltersProvider model={sharedFilters}>
      <MemoAnalyticsSectionInspectionsPresentational
        sectionLabel={formatTranslation(
          intl,
          INSPECTIONS_SECTION_LABEL_ID
        )}
        overviewCharts={overviewCharts}
        runtimeById={runtimeById}
        hasSummarySubsection={hasSummarySubsection}
        summarySubsection={<MemoInspectionSummarySubsection />}
        filters={<MemoSectionFilters model={sharedFilters} />}
        resolveChartTitle={resolveChartTitle}
        resolveChartSubtitle={resolveChartSubtitle}
        isChartId={isCatalogInspectionChartId}
      />
    </SectionFiltersProvider>
  );
}

export const MemoAnalyticsSectionInspections = React.memo(
  AnalyticsSectionInspections
);
